/* Public Smart Form Page — accessed via /f/{token} without authentication. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Briefcase, UserMinus, CheckCircle2, ShieldCheck, AlertCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import DateInputBR from "@/components/DateInputBR";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicFormPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [step, setStep] = useState(0);

  useEffect(() => {
    axios.get(`${BACKEND}/api/forms/public/${token}`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link inválido"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (data) => {
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(
        `${BACKEND}/api/forms/public/${token}/submit`,
        { type: meta.type, data },
      );
      setDone(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally { setSubmitting(false); }
  };

  if (loading) return <FullScreen><Loader2 className="h-8 w-8 animate-spin text-[#234935]" /></FullScreen>;
  if (error) return <FullScreen><div className="text-center"><AlertCircle className="h-10 w-10 text-rose-600 mx-auto mb-3" /><div className="font-semibold">{error}</div></div></FullScreen>;
  if (done) return <SuccessScreen done={done} />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F0] via-white to-[#E7E5DD] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <FormHeader meta={meta} />
        {meta.type === "vacancy_request"
          ? <VacancyForm meta={meta} step={step} setStep={setStep} onSubmit={submit} submitting={submitting} />
          : <TerminationForm meta={meta} step={step} setStep={setStep} onSubmit={submit} submitting={submitting} />
        }
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-[#F8F6F0]">{children}</div>;
}

function FormHeader({ meta }) {
  return (
    <div className="text-center mb-8" data-testid="form-header">
      {meta.company?.logo_url ? (
        <img src={meta.company.logo_url} alt="logo" className="h-12 mx-auto mb-3 object-contain" />
      ) : (
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#234935] to-[#3a7559] text-white mb-3">
          {meta.type === "vacancy_request" ? <Briefcase className="h-7 w-7" /> : <UserMinus className="h-7 w-7" />}
        </div>
      )}
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-bold mb-1">
        {meta.company?.nome_fantasia || "TheoKratos RH"}
      </div>
      <h1 className="text-2xl sm:text-3xl font-bold text-[#234935]">{meta.titulo}</h1>
      <p className="text-[12.5px] text-muted-foreground mt-2">
        Preencha o formulário abaixo. Todas as informações serão enviadas ao RH para análise.
      </p>
    </div>
  );
}

function SuccessScreen({ done }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-white px-4">
      <div className="max-w-md w-full rounded-2xl border border-emerald-200 bg-white shadow-lg p-8 text-center"
           data-testid="success-screen">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
          <CheckCircle2 className="h-9 w-9 text-emerald-700" />
        </div>
        <h2 className="text-2xl font-bold text-[#234935] mb-2">Solicitação enviada!</h2>
        <p className="text-[13px] text-muted-foreground mb-4">{done.message}</p>
        <div className="rounded bg-slate-50 border border-border px-3 py-2 text-[11px]">
          Protocolo: <span className="font-mono font-bold text-[#234935]">{done.protocolo}</span>
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="rounded-2xl border border-border bg-white shadow-md p-6 sm:p-8">{children}</div>;
}

function Field({ label, value, onChange, type, placeholder, required, multi, options }) {
  if (options) {
    return (
      <div className="mb-4">
        <Label className="text-[12px] font-semibold mb-1 block">{label}{required && <span className="text-rose-600 ml-1">*</span>}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="h-11"><SelectValue placeholder="— selecione —" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (multi) {
    return (
      <div className="mb-4">
        <Label className="text-[12px] font-semibold mb-1 block">{label}{required && <span className="text-rose-600 ml-1">*</span>}</Label>
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder} />
      </div>
    );
  }
  return (
    <div className="mb-4">
      <Label className="text-[12px] font-semibold mb-1 block">{label}{required && <span className="text-rose-600 ml-1">*</span>}</Label>
      {type === "date" ? (
        <DateInputBR value={value} onChange={onChange} />
      ) : (
        <Input type={type || "text"} value={value || ""} onChange={(e) => onChange(e.target.value)}
               placeholder={placeholder} className="h-11" />
      )}
    </div>
  );
}

// =================================================================
//  VACANCY FORM (multi-step)
// =================================================================
function VacancyForm({ meta, step, setStep, onSubmit, submitting }) {
  const [d, setD] = useState({
    supervisor_nome: "", supervisor_email: "",
    company_id: meta.company?.id || null, sector_id: "",
    unidade: "",
    cargo: "", quantidade: 1, motivo_contratacao: "aumento_quadro",
    tipo_contratacao: "CLT", horario: "", escala: "",
    salario_proposto: "", beneficios: "", data_prevista: "",
    grau_urgencia: "normal", descricao_atividades: "",
    tem_candidato_definido: false,
    candidato: { nome: "", cpf: "", rg: "", cnh_categoria: "", escolaridade: "",
                 endereco: "", telefone: "", email: "", observacoes: "" },
  });

  const totalSteps = d.tem_candidato_definido ? 4 : 3;
  const canNext = () => {
    if (step === 0) return d.supervisor_nome && d.sector_id;
    if (step === 1) return d.cargo && d.motivo_contratacao && d.tipo_contratacao;
    return true;
  };

  const finalize = () => {
    const payload = { ...d };
    if (payload.sector_id) payload.sector_nome = meta.sectors.find((s) => s.id === payload.sector_id)?.name;
    if (payload.salario_proposto) payload.salario_proposto = parseFloat(payload.salario_proposto);
    payload.quantidade = parseInt(payload.quantidade) || 1;
    onSubmit(payload);
  };

  return (
    <Card>
      <StepHeader step={step} total={totalSteps} />
      {step === 0 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">1. Quem você é</h3>
          <Field label="Seu nome (supervisor)" value={d.supervisor_nome} onChange={(v) => setD({ ...d, supervisor_nome: v })} required />
          <Field label="Seu e-mail" value={d.supervisor_email} onChange={(v) => setD({ ...d, supervisor_email: v })} type="email" />
          <Field label="Setor" value={d.sector_id} onChange={(v) => setD({ ...d, sector_id: v })} required
                 options={meta.sectors.map((s) => ({ v: s.id, l: s.name }))} />
          <Field label="Unidade" value={d.unidade} onChange={(v) => setD({ ...d, unidade: v })} />
        </div>
      )}
      {step === 1 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">2. Dados da vaga</h3>
          <Field label="Cargo" value={d.cargo} onChange={(v) => setD({ ...d, cargo: v })} required />
          <Field label="Quantidade" value={d.quantidade} onChange={(v) => setD({ ...d, quantidade: v })} type="number" />
          <Field label="Motivo da contratação" value={d.motivo_contratacao} onChange={(v) => setD({ ...d, motivo_contratacao: v })} required
                 options={[
                   { v: "substituicao", l: "Substituição" },
                   { v: "aumento_quadro", l: "Aumento de quadro" },
                   { v: "projeto", l: "Projeto específico" },
                   { v: "outros", l: "Outros" },
                 ]} />
          <Field label="Tipo de contratação" value={d.tipo_contratacao} onChange={(v) => setD({ ...d, tipo_contratacao: v })} required
                 options={[
                   { v: "CLT", l: "CLT" }, { v: "PJ", l: "PJ" },
                   { v: "Temporario", l: "Temporário" }, { v: "Aprendiz", l: "Aprendiz" },
                   { v: "Estagio", l: "Estágio" }, { v: "Outros", l: "Outros" },
                 ]} />
          <Field label="Grau de urgência" value={d.grau_urgencia} onChange={(v) => setD({ ...d, grau_urgencia: v })}
                 options={[{ v: "baixa", l: "Baixa" }, { v: "normal", l: "Normal" }, { v: "alta", l: "Alta" }, { v: "critica", l: "Crítica" }]} />
        </div>
      )}
      {step === 2 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">3. Detalhes operacionais</h3>
          <Field label="Horário" value={d.horario} onChange={(v) => setD({ ...d, horario: v })} placeholder="Ex: 08h às 17h" />
          <Field label="Escala" value={d.escala} onChange={(v) => setD({ ...d, escala: v })} placeholder="Ex: 5x2, 12x36..." />
          <Field label="Salário proposto (R$)" value={d.salario_proposto} onChange={(v) => setD({ ...d, salario_proposto: v })} type="number" />
          <Field label="Benefícios" value={d.beneficios} onChange={(v) => setD({ ...d, beneficios: v })} multi placeholder="VR, VT, plano de saúde..." />
          <Field label="Data prevista de início" value={d.data_prevista} onChange={(v) => setD({ ...d, data_prevista: v })} type="date" />
          <Field label="Descrição das atividades" value={d.descricao_atividades} onChange={(v) => setD({ ...d, descricao_atividades: v })} multi />
          <div className="flex items-center gap-2 mt-3 p-3 bg-amber-50/50 border border-amber-200 rounded">
            <Switch checked={d.tem_candidato_definido}
                    onCheckedChange={(v) => setD({ ...d, tem_candidato_definido: v })} />
            <Label className="text-[12.5px] font-medium">Já tenho candidato indicado para essa vaga</Label>
          </div>
        </div>
      )}
      {step === 3 && d.tem_candidato_definido && (
        <div>
          <h3 className="text-lg font-bold mb-2 text-[#234935]">4. Candidato indicado</h3>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5" />
            <div>Estes dados pessoais serão isolados e acessíveis apenas pelo RH para fins de contratação (LGPD).</div>
          </div>
          <Field label="Nome completo" value={d.candidato.nome} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, nome: v } })} />
          <Field label="CPF" value={d.candidato.cpf} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, cpf: v } })} />
          <Field label="RG" value={d.candidato.rg} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, rg: v } })} />
          <Field label="Categoria CNH" value={d.candidato.cnh_categoria} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, cnh_categoria: v } })} />
          <Field label="Escolaridade" value={d.candidato.escolaridade} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, escolaridade: v } })} />
          <Field label="Telefone" value={d.candidato.telefone} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, telefone: v } })} />
          <Field label="E-mail" value={d.candidato.email} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, email: v } })} type="email" />
          <Field label="Endereço" value={d.candidato.endereco} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, endereco: v } })} multi />
          <Field label="Observações" value={d.candidato.observacoes} onChange={(v) => setD({ ...d, candidato: { ...d.candidato, observacoes: v } })} multi />
        </div>
      )}
      <StepNav step={step} setStep={setStep} total={totalSteps} canNext={canNext()} onFinalize={finalize} submitting={submitting} />
    </Card>
  );
}

// =================================================================
//  TERMINATION FORM
// =================================================================
function TerminationForm({ meta, step, setStep, onSubmit, submitting }) {
  const [d, setD] = useState({
    employee_nome: "", employee_matricula: "",
    company_id: meta.company?.id || null, sector_id: "",
    supervisor_nome: "", supervisor_email: "",
    motivo_desligamento: "pedido_demissao",
    justificativa: "", advertencias: "", suspensoes: "", ocorrencias: "",
    deseja_reposicao: false, data_sugerida_desligamento: "",
  });
  const totalSteps = 3;
  const canNext = () => {
    if (step === 0) return d.supervisor_nome && d.sector_id && d.employee_nome;
    if (step === 1) return d.motivo_desligamento && d.justificativa;
    return true;
  };
  const finalize = () => {
    const payload = { ...d };
    if (payload.sector_id) payload.sector_nome = meta.sectors.find((s) => s.id === payload.sector_id)?.name;
    onSubmit(payload);
  };
  return (
    <Card>
      <StepHeader step={step} total={totalSteps} />
      {step === 0 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">1. Identificação</h3>
          <Field label="Seu nome (supervisor)" value={d.supervisor_nome} onChange={(v) => setD({ ...d, supervisor_nome: v })} required />
          <Field label="Seu e-mail" value={d.supervisor_email} onChange={(v) => setD({ ...d, supervisor_email: v })} type="email" />
          <Field label="Setor" value={d.sector_id} onChange={(v) => setD({ ...d, sector_id: v })} required
                 options={meta.sectors.map((s) => ({ v: s.id, l: s.name }))} />
          <Field label="Nome do colaborador" value={d.employee_nome} onChange={(v) => setD({ ...d, employee_nome: v })} required />
          <Field label="Matrícula" value={d.employee_matricula} onChange={(v) => setD({ ...d, employee_matricula: v })} />
        </div>
      )}
      {step === 1 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">2. Motivo e justificativa</h3>
          <Field label="Motivo do desligamento" value={d.motivo_desligamento} onChange={(v) => setD({ ...d, motivo_desligamento: v })} required
                 options={[
                   { v: "pedido_demissao", l: "Pedido de demissão" },
                   { v: "dispensa_sem_justa_causa", l: "Dispensa sem justa causa" },
                   { v: "dispensa_com_justa_causa", l: "Dispensa com justa causa" },
                   { v: "rescisao_acordada", l: "Rescisão acordada" },
                   { v: "termino_contrato_experiencia", l: "Término contrato experiência" },
                   { v: "termino_contrato_determinado", l: "Término contrato determinado" },
                   { v: "aposentadoria", l: "Aposentadoria" },
                 ]} />
          <Field label="Justificativa" value={d.justificativa} onChange={(v) => setD({ ...d, justificativa: v })} multi required />
          <Field label="Data sugerida de desligamento" value={d.data_sugerida_desligamento}
                 onChange={(v) => setD({ ...d, data_sugerida_desligamento: v })} type="date" />
        </div>
      )}
      {step === 2 && (
        <div>
          <h3 className="text-lg font-bold mb-4 text-[#234935]">3. Histórico e reposição</h3>
          <Field label="Advertências" value={d.advertencias} onChange={(v) => setD({ ...d, advertencias: v })} multi />
          <Field label="Suspensões" value={d.suspensoes} onChange={(v) => setD({ ...d, suspensoes: v })} multi />
          <Field label="Ocorrências relevantes" value={d.ocorrencias} onChange={(v) => setD({ ...d, ocorrencias: v })} multi />
          <div className="flex items-center gap-2 mt-3 p-3 bg-sky-50/50 border border-sky-200 rounded">
            <Switch checked={d.deseja_reposicao} onCheckedChange={(v) => setD({ ...d, deseja_reposicao: v })} />
            <Label className="text-[12.5px] font-medium">Desejo reposição desta vaga</Label>
          </div>
        </div>
      )}
      <StepNav step={step} setStep={setStep} total={totalSteps} canNext={canNext()} onFinalize={finalize} submitting={submitting} />
    </Card>
  );
}

function StepHeader({ step, total }) {
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-1.5 text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
        <span>Passo {step + 1} de {total}</span>
        <span>{Math.round(((step + 1) / total) * 100)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#234935] to-[#C5A059] transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
      </div>
    </div>
  );
}

function StepNav({ step, setStep, total, canNext, onFinalize, submitting }) {
  return (
    <div className="flex justify-between mt-6 pt-4 border-t border-border">
      <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
        Voltar
      </Button>
      {step < total - 1 ? (
        <Button onClick={() => setStep(step + 1)} disabled={!canNext}
                className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="step-next">
          Próximo
        </Button>
      ) : (
        <Button onClick={onFinalize} disabled={submitting} className="bg-emerald-700 hover:bg-emerald-800" data-testid="step-finalize">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
          Enviar Solicitação
        </Button>
      )}
    </div>
  );
}
